import { IncentiveOfferSellerParticipationLimits } from 'modules/incentivesMarket/types';
import {
  IncentiveOfferSellerOfferCap,
  IncentivesMarketPlanContext,
} from '../types/IncentivesMarketBeneficiaryContext';

import { calculateMaxUnitsSellerCanOfferFromPlans } from './calculateSellerSellLimits';

export const calculatePreviewMaxUnitsSellerCanOffer = ({
  sellerParticipationLimits,
  totalAvailableUnits,
  unitPrice = 0,
  planType = 'STOCK_OPTION',
  offerRemainingUnits,
  plans,
}: {
  sellerParticipationLimits?: IncentiveOfferSellerParticipationLimits;
  totalAvailableUnits: number;
  unitPrice?: number;
  planType?: string;
  offerRemainingUnits?: number;
  plans?: IncentivesMarketPlanContext[];
}): number =>
  calculateMaxUnitsSellerCanOfferFromPlans({
    sellerParticipationLimits,
    plans,
    totalAvailableUnits,
    unitPrice,
    planType,
    offerRemainingUnits,
  });

export const getSellerLimitsModeLabelKey = (
  limits?: IncentiveOfferSellerParticipationLimits
): string =>
  limits?.allowSellAll === false
    ? 'IncentivesMarketSellerLimitsCustom'
    : 'IncentivesMarketSellerLimitsNoCap';

const normalizeLimits = (
  limits?: IncentiveOfferSellerParticipationLimits
): Required<IncentiveOfferSellerParticipationLimits> => ({
  allowSellAll: limits?.allowSellAll !== false,
  maxUnits: limits?.maxUnits || 0,
  maxPercentUnits: limits?.maxPercentUnits || 0,
  maxPhantoms: limits?.maxPhantoms || 0,
  maxPercentPhantoms: limits?.maxPercentPhantoms || 0,
  maxStocks: limits?.maxStocks || 0,
  maxPercentStocks: limits?.maxPercentStocks || 0,
  maxWarrants: limits?.maxWarrants || 0,
  maxPercentWarrants: limits?.maxPercentWarrants || 0,
  maxAmount: limits?.maxAmount || 0,
  maxPercentUnvested:
    limits?.maxPercentUnvested === undefined ||
    limits?.maxPercentUnvested === null
      ? 100
      : limits.maxPercentUnvested,
});

export const sanitizeSellerParticipationLimitsForPayload = (
  limits?: IncentiveOfferSellerParticipationLimits
): IncentiveOfferSellerParticipationLimits => {
  const normalized = normalizeLimits(limits);

  if (normalized.allowSellAll) {
    return { allowSellAll: true, maxPercentUnvested: 100 };
  }

  const result: IncentiveOfferSellerParticipationLimits = {
    allowSellAll: false,
    maxPercentUnvested: normalized.maxPercentUnvested,
  };

  if (normalized.maxUnits > 0) result.maxUnits = normalized.maxUnits;
  if (normalized.maxPercentUnits > 0) {
    result.maxPercentUnits = normalized.maxPercentUnits;
  }
  if (normalized.maxPhantoms > 0) result.maxPhantoms = normalized.maxPhantoms;
  if (normalized.maxPercentPhantoms > 0) {
    result.maxPercentPhantoms = normalized.maxPercentPhantoms;
  }
  if (normalized.maxStocks > 0) result.maxStocks = normalized.maxStocks;
  if (normalized.maxPercentStocks > 0) {
    result.maxPercentStocks = normalized.maxPercentStocks;
  }
  if (normalized.maxWarrants > 0) result.maxWarrants = normalized.maxWarrants;
  if (normalized.maxPercentWarrants > 0) {
    result.maxPercentWarrants = normalized.maxPercentWarrants;
  }
  if (normalized.maxAmount > 0) result.maxAmount = normalized.maxAmount;

  return result;
};

export const buildSellerOfferCapFromContext = (
  sellerOfferCap?: IncentiveOfferSellerOfferCap,
  offerRemainingUnits = 0,
  totalAvailableUnits = 0
): IncentiveOfferSellerOfferCap => {
  if (sellerOfferCap) {
    return sellerOfferCap;
  }

  const remaining = Math.max(0, offerRemainingUnits);
  const effectiveMax = Math.min(Math.max(0, totalAvailableUnits), remaining);

  return {
    maxTotalSellAmount: 0,
    maxUnitsSellerCanOffer: effectiveMax,
    offerRemainingUnits: remaining,
    effectiveMax,
  };
};
